<template>
    <div class="relativeContainer" ref="cvMapContainer">
        <SystemMessage :message="errorMessage" :dismissButton="false" v1 v-if="!!errorMessage"></SystemMessage>
        <Loader text="Loading" :status="loading"></Loader>
        <div class="wrap">
            <div class="fixed-header">
                <div class="background-cover"></div>
                <CVManagerEditHeader
                        :name.sync="name"
                        :enableSDKSetCV.sync="enableSDKSetCV"
                        :selectedApps.sync="apps"
                        :cvMapId="cvIdNumber"
                        :selectedLoaded="cvLoaded"
                        ref="header"
                        @save="handleSave"
                        @cancel="handleCancel"
                        @error="errorMessage = error"
                        @importSteps="steps = $event"
                />
                <CVWizardHeader/>
            </div>
            <div class="content">
                <CVManagerCVMap
                    :steps.sync="steps"
                    :readonly="false"
                    :apps="apps"
                    :cvLoaded="cvLoaded"
                    ref="cvMap"
                    :scrollerRef="cvMapContainer"
                    :virtualScroll="false"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import consts from 'services/metaData/consts';
import Loader from 'components/loader/loader.vue';
import { isEqual, cloneDeep } from 'lodash';
import CVManagerEditHeader from 'pages/conversionValue/cvManager/components/CVManagerEditHeader.vue';
import CVManagerCVMap from 'pages/conversionValue/cvManager/components/CVManagerCVMap.vue';
import CVWizardHeader from 'pages/conversionValue/cvManager/components/CVWizardHeader.vue';
import {
    apiKey,
    getDataForIdAPIPath,
    saveCVMap,
    formatSteps,
    formatCVMapResponse
} from 'pages/conversionValue/cvManager/serviceUtils';
import monetizeAppService from 'services/monetizeApp';

const defaultCVMapSteps = [
    { key: 0, min: 0, max: null }
];

export default {
    name: 'CVManagerNewEditPage',
    components: {
        Loader,
        CVWizardHeader,
        CVManagerEditHeader,
        CVManagerCVMap,
        SystemMessage
    },
    props: {
        id: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            loading: false,
            name: null,
            nameOriginal: null,
            enableSDKSetCV: true,
            enableSDKSetCVOriginal: false,
            apps: [],
            appsOriginal: [],
            steps: this.getDefaultSteps(),
            stepsOriginal: this.getDefaultSteps(),
            formDirty: false,
            errorMessage: '',
            cvLoaded: false,
            cvMapContainer: null
        };
    },
    async created() {
        this.setPage({
            pageTitle: `${this.isNew ? 'Add New' : 'Edit'} CV Map`
        });
        window.addEventListener('beforeunload', this.beforeWindowUnload);

        if (!this.isNew) {
            this.loading = true;
            await this.getCVData();
            this.loading = false;
        }
        this.cvLoaded = true;
    },
    mounted() {
        this.cvMapContainer = this.$refs.cvMapContainer;
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
    },
    async beforeRouteLeave(to, from, next) {
        // If the form is dirty and the user did not confirm leave,
        // prevent losing unsaved changes by canceling navigation
        const confirmStay = await this.confirmStayInDirtyFormRoute();
        if (confirmStay) {
            next(false);
        } else {
            // Navigate to next view
            next();
        }
    },
    watch: {
        name() {
            this.markFormDirty();
        },
        enableSDKSetCV() {
            this.markFormDirty();
        },
        apps() {
            this.markFormDirty();
        },
        steps() {
            this.markFormDirty();
        }
    },
    computed: {
        isNew() {
            return this.id === 'new';
        },
        cvIdNumber() {
            return !Number.isNaN(this.id) ? parseInt(this.id, 10) : this.id;
        }
    },
    methods: {
        ...mapMutations({
            setPage: 'setPage',
            clearCache: 'cache/clearCache'
        }),
        ...mapActions({
            getWithCache: 'cache/getWithCache'
        }),
        getDefaultSteps() {
            return defaultCVMapSteps.slice().map((item) => ({ ...item }));
        },
        async getCVData() {
            try {
                const response = await this.getWithCache({
                    api: apiKey,
                    req: getDataForIdAPIPath(this.id)
                });
                const cvData = response.data;
                this.apps = cvData.apps.map((app) => app.appKey);
                this.appsOriginal = this.apps.slice();
                this.enableSDKSetCV = cvData.enableSDKSetCV;
                this.enableSDKSetCVOriginal = cvData.enableSDKSetCV;
                this.steps = formatCVMapResponse(cvData.steps);
                this.stepsOriginal = cloneDeep(this.steps);
                this.name = cvData.name;
                this.nameOriginal = cvData.name;
            } catch (error) {
                this.errorMessage = error.response?.data?.errorMessage || consts.GENERAL_SERVICE_ERROR;
            }
        },
        async handleSave() {
            this.$refs.cvMap.$emit('validateSteps');
            if (this.$refs.cvMap.$v.$anyError) {
                return;
            }
            if (this.$refs.header.$v.$anyError) {
                return;
            }

            let newId = null;
            try {
                this.loading = true;
                const response = await saveCVMap({
                    id: this.id,
                    name: this.name,
                    steps: formatSteps(this.steps),
                    enableSDKSetCV: this.enableSDKSetCV,
                    apps: this.apps
                });
                const newSchema = response.data;
                newId = newSchema.id;
                await this.clearCache({ api: apiKey });
                this.formDirty = false;
                monetizeAppService.clearData();
                await this.$router.push({ path: `/conversionValue/cvManager/${this.isNew ? newId : this.id}` });
            } catch (error) {
                this.errorMessage = error.response?.data?.errorMessage || consts.GENERAL_SERVICE_ERROR;
            }
            this.loading = false;
        },
        async handleCancel() {
            await (this.$router.push({ path: `/conversionValue/cvManager${this.isNew ? '' : `/${this.id}`}` })
                .catch((err) => err));
        },
        markFormDirty() {
            this.formDirty = (!isEqual(this.apps, this.appsOriginal)
                || this.name !== this.nameOriginal
                || this.enableSDKSetCV !== this.enableSDKSetCVOriginal
                || !isEqual(this.steps, this.stepsOriginal)
            );
        },
        confirmLeave() {
            // eslint-disable-next-line no-alert
            return window.confirm();
        },
        async confirmLeaveRoute() {
            return new Promise((resolve) => {
                this.$modal.open({
                    title: 'Leave Page',
                    body: 'The changes you made will not be saved, are you sure you want to leave this page?',
                    okButtonText: 'Leave page',
                    cancelButtonText: 'Cancel',
                    version: 1,
                    type: 'general',
                    onOK: () => {
                        resolve(true);
                    },
                    onClose: () => {
                        resolve(false);
                    }
                });
            });
        },
        confirmStayInDirtyForm() {
            return this.formDirty && !this.confirmLeave();
        },
        async confirmStayInDirtyFormRoute() {
            return this.formDirty && !(await this.confirmLeaveRoute());
        },
        beforeWindowUnload(e) {
            if (this.confirmStayInDirtyForm()) {
                // Cancel the event
                e.preventDefault();

                // Chrome requires returnValue to be set
                e.returnValue = '';
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import "~styles/styleGuide";

@mixin boxWidth {
    width: 752px;

    @media #{$desktopAndAbove} {
        width: 1100px;
    }
}

.wrap {
    position: relative;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 20px;
    @include boxWidth;
}

.fixed-header {
    position: fixed;
    margin-top: 24px;
    @include boxWidth;
    z-index: 1;
}

.content {
    position: relative;
    padding-top: 179px;
}

.background-cover {
    height: 24px;
    margin-top: -24px;
    background-color: $light-200;
}
</style>
